import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import DeductionForm, { useCatalog } from "@/components/DeductionForm";
import { fmtTL, fmtDate } from "@/lib/utils";

export default function DeductionDialog({ payment, onClose, onSaved }) {
  const catalog = useCatalog();
  const [list, setList] = useState([]);
  const [sel, setSel] = useState([]);
  const [pct, setPct] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!payment) return;
    const params = payment.firm_id ? { firm_id: payment.firm_id } : { no_firm: true };
    api.get("/deductions", { params }).then(r => {
      const rows = r.data.filter(d => d.status === "bekliyor" || d.payment_id === payment.id);
      setList(rows);
      setSel(rows.filter(d => d.payment_id === payment.id).map(d => d.id));
    });
    const p = {};
    (payment.deduction_items || []).filter(d => d.rate).forEach(d => { p[d.category] = { on: true, rate: d.rate }; });
    setPct(p);
  }, [payment]);

  if (!payment) return null;
  const gross = payment.gross_amount || 0;
  const percents = catalog.filter(c => c.type === "percent");
  const pctTotal = percents.reduce((s, c) => s + (pct[c.category]?.on ? gross * (Number(pct[c.category].rate) || 0) / 100 : 0), 0);
  const selTotal = list.filter(d => sel.includes(d.id)).reduce((s, d) => s + d.amount, 0);
  const total = pctTotal + selTotal;
  const groups = list.reduce((a, d) => ({ ...a, [d.category]: [...(a[d.category] || []), d] }), {});

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/progress-payments/${payment.id}/deductions`, {
        deduction_ids: sel,
        percent_items: percents.filter(c => pct[c.category]?.on).map(c => ({ category: c.category, name: `%${String(pct[c.category].rate).replace(".", ",")}`, rate: Number(pct[c.category].rate) || 0 })),
      });
      toast.success("Kesintiler hakedişe uygulandı"); onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="deduction-dialog">
        <DialogHeader><DialogTitle className="font-display">Kesintiler — {payment.firm_name || "Hakediş"} #{payment.payment_no}</DialogTitle></DialogHeader>
        <div className="text-xs text-muted-foreground -mt-2">Dönem: {fmtDate(payment.period_start)} — {fmtDate(payment.period_end)} · Brüt: <strong className="text-foreground">{fmtTL(gross)}</strong></div>

        {percents.map(c => (
          <div key={c.category} className="rounded-lg border border-border p-3 flex items-center gap-3" data-testid="percent-deduction">
            <Checkbox checked={!!pct[c.category]?.on} onCheckedChange={v => setPct({ ...pct, [c.category]: { rate: pct[c.category]?.rate ?? c.rate, on: !!v } })} data-testid="percent-deduction-check" />
            <div className="flex-1">
              <div className="text-sm font-semibold">{c.category}</div>
              <div className="text-[11px] text-muted-foreground">{c.items.join(" • ")}</div>
            </div>
            <span className="text-sm">%</span>
            <Input type="number" step="0.1" className="w-20" value={pct[c.category]?.rate ?? c.rate} onChange={e => setPct({ ...pct, [c.category]: { on: pct[c.category]?.on ?? true, rate: e.target.value } })} data-testid="percent-deduction-rate" />
            <span className="font-mono-nums text-sm w-32 text-right">{fmtTL(pct[c.category]?.on ? gross * (Number(pct[c.category]?.rate) || 0) / 100 : 0)}</span>
          </div>
        ))}

        <div className="space-y-3">
          {Object.keys(groups).length === 0 && <p className="text-sm text-muted-foreground">Bu firmaya ait bekleyen kesinti yok. Aşağıdan ekleyebilirsiniz.</p>}
          {Object.entries(groups).map(([cat, rows]) => (
            <div key={cat} className="rounded-lg border border-border">
              <div className="px-3 py-2 bg-muted/40 text-xs font-bold flex justify-between">
                <span>{cat}</span>
                <button type="button" className="text-amber-700 dark:text-amber-400" onClick={() => {
                  const ids = rows.map(r => r.id); const all = ids.every(i => sel.includes(i));
                  setSel(all ? sel.filter(i => !ids.includes(i)) : [...new Set([...sel, ...ids])]);
                }}>Tümünü seç / bırak</button>
              </div>
              {rows.map(d => (
                <label key={d.id} className="flex items-center gap-3 px-3 py-2 text-sm border-t border-border cursor-pointer" data-testid={`deduction-row-${d.id}`}>
                  <Checkbox data-testid={`deduction-check-${d.id}`} checked={sel.includes(d.id)} onCheckedChange={v => setSel(v ? [...sel, d.id] : sel.filter(x => x !== d.id))} />
                  <span className="flex-1">{d.name}{d.notes && <span className="text-xs text-muted-foreground"> · {d.notes}</span>}</span>
                  <span className="text-xs text-muted-foreground w-24">{fmtDate(d.period)}</span>
                  <span className="font-mono-nums w-32 text-right">{fmtTL(d.amount)}</span>
                </label>
              ))}
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-dashed border-border p-3">
          <div className="text-xs font-semibold mb-2">Yeni kesinti ekle</div>
          <DeductionForm compact firmId={payment.firm_id || ""} onCreated={d => { setList([...list, d]); setSel([...sel, d.id]); }} />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Sum label="Brüt Hakediş" value={fmtTL(gross)} />
          <Sum label="Toplam Kesinti" value={`-${fmtTL(total)}`} testid="deduction-total" red />
          <Sum label="Net Ödenecek" value={fmtTL(gross - total)} testid="deduction-net" accent />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="deduction-save">Kesintileri Uygula</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Sum({ label, value, testid, red, accent }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? "border-amber-500/40 bg-amber-500/10" : "border-border"}`} data-testid={testid}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-mono-nums font-bold ${red ? "text-rose-600" : ""}`}>{value}</div>
    </div>
  );
}
